// CAPABILITY: Live chat stream + send/name wiring for a public page.
// INPUTS: pageId, enabled
// OUTPUTS: { messages, sending, send(), updateName(), displayName }
// Reads published messages directly via Firestore onSnapshot (rules allow
// status=="published" reads). Writes go through the gated HTTP functions.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  collection,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { firestore } from "../../firebase";
import { sendChatMessage, setChatDisplayName } from "../api/chatClient";
import {
  getCachedDisplayName,
  getChatViewerId,
  setCachedDisplayName,
} from "../utils/chatIdentity";

const MAX_VISIBLE_MESSAGES = 100;

export function useChatStream({ pageId, enabled }) {
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [displayName, setDisplayName] = useState(() => getCachedDisplayName());
  const viewerIdRef = useRef(null);

  if (viewerIdRef.current === null) viewerIdRef.current = getChatViewerId();

  // Live subscription to published messages for this page.
  useEffect(() => {
    if (!enabled || !pageId) {
      setMessages([]);
      return undefined;
    }

    const q = query(
      collection(firestore, "chat_messages"),
      where("page_id", "==", pageId),
      where("status", "==", "published"),
      orderBy("created_at", "asc"),
      limitToLast(MAX_VISIBLE_MESSAGES)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.warn(
          "CHAT_STREAM_FAILED",
          `page_id=${pageId} code=${error?.code || "unknown"} message=${error?.message || ""}`
        );
      }
    );

    console.info("CHAT_STREAM_SUBSCRIBED", `page_id=${pageId}`);
    return () => unsubscribe();
  }, [enabled, pageId]);

  const send = useCallback(
    async (text) => {
      if (!pageId || !text?.trim()) return { ok: false, reason_code: "empty" };
      setSending(true);
      try {
        const result = await sendChatMessage({
          pageId,
          viewerId: viewerIdRef.current,
          displayName: displayName || "Guest",
          text,
        });
        return result;
      } finally {
        setSending(false);
      }
    },
    [pageId, displayName]
  );

  const updateName = useCallback(
    async (name) => {
      if (!pageId) return { ok: false, reason_code: "no_page" };
      const result = await setChatDisplayName({
        pageId,
        viewerId: viewerIdRef.current,
        name,
      });
      if (result.ok && result.name_status === "approved" && result.displayName) {
        setDisplayName(result.displayName);
        setCachedDisplayName(result.displayName);
      }
      return result;
    },
    [pageId]
  );

  return { messages, sending, displayName, send, updateName };
}

export { MAX_VISIBLE_MESSAGES };
